import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import Select from "react-select";

import ai from "../../helpers/axios";
import "./seasonal-pricing.css";
import getApiErrorMessage from "../../helpers/get-api-error-message";

const HOT_THRESHOLD = 0.1; // ≥ +10% → hot banner

// Fallback 50-state list — used if the /states endpoint is unreachable.
// The endpoint-returned list is preferred (filtered to US states that
// actually have airports in our DB) but we never want the form to be
// unusable because of a server hiccup.
const US_STATE_NAMES = {
  "US-AL": "Alabama",     "US-AK": "Alaska",        "US-AZ": "Arizona",
  "US-AR": "Arkansas",    "US-CA": "California",    "US-CO": "Colorado",
  "US-CT": "Connecticut", "US-DE": "Delaware",      "US-DC": "District of Columbia",
  "US-FL": "Florida",     "US-GA": "Georgia",       "US-HI": "Hawaii",
  "US-ID": "Idaho",       "US-IL": "Illinois",      "US-IN": "Indiana",
  "US-IA": "Iowa",        "US-KS": "Kansas",        "US-KY": "Kentucky",
  "US-LA": "Louisiana",   "US-ME": "Maine",         "US-MD": "Maryland",
  "US-MA": "Massachusetts", "US-MI": "Michigan",    "US-MN": "Minnesota",
  "US-MS": "Mississippi", "US-MO": "Missouri",      "US-MT": "Montana",
  "US-NE": "Nebraska",    "US-NV": "Nevada",        "US-NH": "New Hampshire",
  "US-NJ": "New Jersey",  "US-NM": "New Mexico",    "US-NY": "New York",
  "US-NC": "North Carolina", "US-ND": "North Dakota", "US-OH": "Ohio",
  "US-OK": "Oklahoma",    "US-OR": "Oregon",        "US-PA": "Pennsylvania",
  "US-RI": "Rhode Island", "US-SC": "South Carolina", "US-SD": "South Dakota",
  "US-TN": "Tennessee",   "US-TX": "Texas",         "US-UT": "Utah",
  "US-VT": "Vermont",     "US-VA": "Virginia",      "US-WA": "Washington",
  "US-WV": "West Virginia", "US-WI": "Wisconsin",   "US-WY": "Wyoming",
};

const US_STATES_FALLBACK = Object.keys(US_STATE_NAMES);

const stateLabel = (code) => {
  const name = US_STATE_NAMES[code];
  const short = code.replace(/^US-/, "");
  return name ? `${short} — ${name}` : code;
};

// ─── formatters ──────────────────────────────────────────────────────────────

const formatAdjustment = (adj) => {
  if (adj === null || adj === undefined) return "—";
  const pct = (adj * 100).toFixed(adj * 100 === Math.trunc(adj * 100) ? 0 : 2);
  return `${adj > 0 ? "+" : ""}${pct}%`;
};

// Rule dates are stored as UTC midnight (e.g. `2026-11-01T00:00:00.000Z`)
// representing a calendar date, not a specific moment. We must format them
// in UTC — otherwise local-time formatting (EST = UTC-5) rolls midnight back
// to the previous calendar day.
const toInputDate = (d) => {
  if (!d) return "";
  const m = moment.utc(d);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

const formatMonth = (d) => moment.utc(d).format("MMM");
const formatDay = (d) => moment.utc(d).format("D");
const formatYear = (d) => moment.utc(d).format("YYYY");

const bannerModifier = (rule) => {
  if (!rule.active) return "inactive";
  if (rule.adjustment < 0) return "down";
  if (rule.adjustment >= HOT_THRESHOLD) return "up-hot";
  return "up";
};

const bannerLabel = (rule) => {
  if (!rule.active) return "Paused";
  if (rule.adjustment < 0) return "Discount";
  if (rule.adjustment >= HOT_THRESHOLD) return "Peak Surcharge";
  return "Surcharge";
};

// ─── form defaults ───────────────────────────────────────────────────────────

const emptyForm = {
  _id: null,
  label: "",
  startDate: "",
  endDate: "",
  departureStates: [],
  arrivalStates: [],
  adjustmentPct: "",
  explainer: "",
  active: true,
};

// ─── state chips (collapsible overflow) ──────────────────────────────────────

const StateChips = ({ states }) => {
  const [expanded, setExpanded] = useState(false);

  if (!states || !states.length) {
    return <span style={{ color: "var(--sp-text-tertiary)" }}>—</span>;
  }
  const MAX = 8;
  const showAll = expanded || states.length <= MAX;
  const visible = showAll ? states : states.slice(0, MAX);
  const overflow = states.length - MAX;
  return (
    <div className="sp-rule__states">
      {visible.map((s) => (
        <span key={s} className="sp-rule__state-chip">
          {s.replace(/^US-/, "")}
        </span>
      ))}
      {!showAll && overflow > 0 && (
        <button
          type="button"
          className="sp-rule__state-chip sp-rule__state-chip--overflow"
          onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
          style={{ cursor: "pointer", border: "1px solid var(--sp-border)" }}
        >
          +{overflow} more
        </button>
      )}
      {expanded && states.length > MAX && (
        <button
          type="button"
          className="sp-rule__state-chip sp-rule__state-chip--overflow"
          onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
          style={{ cursor: "pointer", border: "1px solid var(--sp-border)" }}
        >
          Show less
        </button>
      )}
    </div>
  );
};

// ─── main component ─────────────────────────────────────────────────────────

const SeasonalPricingContent = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [rules, setRules] = useState([]);
  // Seed with the fallback 50-state list so the autocomplete is usable
  // immediately — the /states endpoint then narrows this to the US states
  // that actually have airports in our DB.
  const [states, setStates] = useState(US_STATES_FALLBACK);
  const [isLoadingRules, setIsLoadingRules] = useState(true);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [filter, setFilter] = useState("all"); // all | active | inactive

  useEffect(() => {
    document.title = "Seasonal Pricing | evoJets HQ";
  }, []);

  const getToken = useCallback(async () => {
    try {
      return await getAccessTokenSilently();
    } catch (e) {
      console.error(e);
      loginWithRedirect();
      return null;
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const loadRules = useCallback(async () => {
    setIsLoadingRules(true);
    try {
      const token = await getToken();
      if (!token) return;
      const response = await ai.auth(token).get(`/api/seasonalPricing`);
      if (response?.data?.data) {
        setRules(response.data.data);
      }
    } catch (error) {
      console.error("loadRules failed", error);
      Sentry.captureException(error);
      toast.error("Failed to load seasonal pricing rules.");
    } finally {
      setIsLoadingRules(false);
    }
  }, [getToken]);

  const loadStates = useCallback(async () => {
    setIsLoadingStates(true);
    try {
      const token = await getToken();
      if (!token) return;
      const response = await ai.auth(token).get(`/api/seasonalPricing/states`);
      const fetched = response?.data?.data;
      console.log("[SeasonalPricing] /states response:", fetched);
      // Only replace the fallback if the endpoint returned a usable list.
      if (Array.isArray(fetched) && fetched.length > 0) {
        setStates(fetched);
      } else {
        console.warn(
          "[SeasonalPricing] /states returned empty or invalid; keeping fallback 50-state list"
        );
      }
    } catch (error) {
      console.error(
        "[SeasonalPricing] loadStates failed — falling back to hardcoded 50-state list",
        error?.response?.status,
        error?.response?.data || error?.message
      );
      Sentry.captureException(error);
    } finally {
      setIsLoadingStates(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadRules();
    loadStates();
  }, [loadRules, loadStates]);

  // ─── derived ─────────────────────────────────────────────

  const stateOptions = useMemo(
    () =>
      (states || [])
        .slice()
        .sort()
        .map((s) => ({ value: s, label: stateLabel(s) })),
    [states]
  );

  const departureValue = useMemo(
    () =>
      form.departureStates.map((s) => ({ value: s, label: stateLabel(s) })),
    [form.departureStates]
  );

  const arrivalValue = useMemo(
    () => form.arrivalStates.map((s) => ({ value: s, label: stateLabel(s) })),
    [form.arrivalStates]
  );

  const visibleRules = useMemo(() => {
    if (filter === "active") return rules.filter((r) => r.active);
    if (filter === "inactive") return rules.filter((r) => !r.active);
    return rules;
  }, [rules, filter]);

  const stats = useMemo(() => {
    const active = rules.filter((r) => r.active).length;
    const inactive = rules.length - active;
    return {
      total: rules.length,
      active,
      inactive,
    };
  }, [rules]);

  // ─── handlers ────────────────────────────────────────────

  const resetForm = useCallback(() => setForm(emptyForm), []);

  const handleNewRule = useCallback(() => {
    resetForm();
    setFormOpen(true);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [resetForm]);

  const handleEdit = useCallback((rule) => {
    setForm({
      _id: rule._id,
      label: rule.label || "",
      startDate: toInputDate(rule.startDate),
      endDate: toInputDate(rule.endDate),
      departureStates: rule.departureStates || [],
      arrivalStates: rule.arrivalStates || [],
      adjustmentPct:
        rule.adjustment !== undefined && rule.adjustment !== null
          ? (rule.adjustment * 100).toString()
          : "",
      explainer: rule.explainer || "",
      active: rule.active !== undefined ? rule.active : true,
    });
    setFormOpen(true);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const handleCancelForm = useCallback(() => {
    resetForm();
    setFormOpen(false);
  }, [resetForm]);

  const handleSubmit = useCallback(
    async (e) => {
      if (e && e.preventDefault) e.preventDefault();

      if (!form.label.trim()) {
        toast.error("Label is required.");
        return;
      }
      if (!form.startDate || !form.endDate) {
        toast.error("Start and end dates are required.");
        return;
      }
      if (new Date(form.endDate) < new Date(form.startDate)) {
        toast.error("End date must be on or after start date.");
        return;
      }
      if (form.departureStates.length === 0) {
        toast.error("Select at least one departure state.");
        return;
      }
      if (form.arrivalStates.length === 0) {
        toast.error("Select at least one arrival state.");
        return;
      }
      const adjustmentPctNum = parseFloat(form.adjustmentPct);
      if (Number.isNaN(adjustmentPctNum)) {
        toast.error("Adjustment % must be a number.");
        return;
      }
      const adjustment = adjustmentPctNum / 100;
      if (adjustment < -1 || adjustment > 1) {
        toast.error("Adjustment must be between -100% and 100%.");
        return;
      }

      setIsSaving(true);
      try {
        const token = await getToken();
        if (!token) return;

        const payload = {
          label: form.label.trim(),
          startDate: form.startDate,
          endDate: form.endDate,
          departureStates: form.departureStates,
          arrivalStates: form.arrivalStates,
          adjustment,
          explainer: form.explainer.trim(),
          active: form.active,
        };

        const response = form._id
          ? await ai
              .auth(token)
              .put(`/api/seasonalPricing/${form._id}`, payload)
          : await ai.auth(token).post(`/api/seasonalPricing`, payload);

        if (response?.data) {
          toast.success(form._id ? "Rule updated." : "Rule created.");
          resetForm();
          setFormOpen(false);
          await loadRules();
        }
      } catch (error) {
        console.error("save failed", error);
        Sentry.captureException(error);
        toast.error(
          getApiErrorMessage(error, "Failed to save seasonal pricing rule.")
        );
      } finally {
        setIsSaving(false);
      }
    },
    [form, getToken, loadRules, resetForm]
  );

  const handleDelete = useCallback(
    async (rule) => {
      if (!window.confirm(`Delete rule "${rule.label}"?`)) return;
      try {
        const token = await getToken();
        if (!token) return;
        const response = await ai
          .auth(token)
          .delete(`/api/seasonalPricing/${rule._id}`);
        if (response?.data?.success) {
          toast.success("Rule deleted.");
          if (form._id === rule._id) {
            resetForm();
            setFormOpen(false);
          }
          await loadRules();
        }
      } catch (error) {
        console.error("delete failed", error);
        Sentry.captureException(error);
        toast.error("Failed to delete rule.");
      }
    },
    [form._id, getToken, loadRules, resetForm]
  );

  const handleToggleActive = useCallback(
    async (rule) => {
      try {
        const token = await getToken();
        if (!token) return;
        const payload = {
          label: rule.label,
          startDate: toInputDate(rule.startDate),
          endDate: toInputDate(rule.endDate),
          departureStates: rule.departureStates,
          arrivalStates: rule.arrivalStates,
          adjustment: rule.adjustment,
          active: !rule.active,
        };
        const response = await ai
          .auth(token)
          .put(`/api/seasonalPricing/${rule._id}`, payload);
        if (response?.data) {
          await loadRules();
        }
      } catch (error) {
        console.error("toggle failed", error);
        Sentry.captureException(error);
        toast.error("Failed to update rule.");
      }
    },
    [getToken, loadRules]
  );

  // ─── render ──────────────────────────────────────────────

  const isEditing = !!form._id;

  return (
    <div className="sp-page">
      {/* HEADER */}
      <div className="sp__header">
        <div className="sp__header-row">
          <div>
            <div className="sp__header-pretitle">Settings</div>
            <h1 className="sp__header-title">Seasonal Pricing</h1>
            <p className="sp__header-description">
              Directional pricing adjustments applied after base, peak, and
              special-event markups. Rules match by date range and origin →
              arrival state. Overlapping rules sum their adjustments.
            </p>
          </div>

          <div className="sp__header-stats">
            <div className="sp__header-stat">
              <div className="sp__header-stat-value">{stats.total}</div>
              <div className="sp__header-stat-label">Total Rules</div>
            </div>
            <div className="sp__header-stat">
              <div className="sp__header-stat-value">{stats.active}</div>
              <div className="sp__header-stat-label">Active</div>
            </div>
            <div className="sp__header-stat">
              <div className="sp__header-stat-value">{stats.inactive}</div>
              <div className="sp__header-stat-label">Paused</div>
            </div>
          </div>
        </div>

        <div className="sp__filter-bar">
          <div className="sp__filter-pills">
            <button
              className={`sp__filter-pill${
                filter === "all" ? " sp__filter-pill--active" : ""
              }`}
              onClick={() => setFilter("all")}
            >
              All
            </button>
            <button
              className={`sp__filter-pill${
                filter === "active" ? " sp__filter-pill--active" : ""
              }`}
              onClick={() => setFilter("active")}
            >
              Active
            </button>
            <button
              className={`sp__filter-pill${
                filter === "inactive" ? " sp__filter-pill--active" : ""
              }`}
              onClick={() => setFilter("inactive")}
            >
              Paused
            </button>
          </div>

          <div className="sp__toolbar">
            {!formOpen && (
              <button
                type="button"
                className="sp__primary-btn"
                onClick={handleNewRule}
              >
                <span className="sp__primary-btn__plus">+</span>
                New Rule
              </button>
            )}
          </div>
        </div>
      </div>

      {/* FORM */}
      {formOpen && (
        <div className="sp__form-card">
          <div
            className={`sp__form-banner${
              isEditing ? " sp__form-banner--edit" : ""
            }`}
          >
            <span>{isEditing ? "Edit Rule" : "New Seasonal Rule"}</span>
            <span style={{ opacity: 0.7, fontSize: 11 }}>
              {isEditing ? `ID · ${form._id.slice(-6)}` : "DRAFT"}
            </span>
          </div>

          <form onSubmit={handleSubmit} className="sp__form-body">
            <div className="sp__form-row">
              <div>
                <label className="sp__field-label">
                  Label
                  <span className="sp__field-label-required">*</span>
                </label>
                <input
                  type="text"
                  className="sp__input"
                  placeholder="e.g. Snowbird Fall NE → SE 2026"
                  value={form.label}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, label: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="sp__form-row sp__form-row--three">
              <div>
                <label className="sp__field-label">
                  Start Date
                  <span className="sp__field-label-required">*</span>
                </label>
                <input
                  type="date"
                  className="sp__date-input"
                  value={form.startDate}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, startDate: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="sp__field-label">
                  End Date
                  <span className="sp__field-label-required">*</span>
                </label>
                <input
                  type="date"
                  className="sp__date-input"
                  value={form.endDate}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, endDate: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="sp__field-label">
                  Adjustment
                  <span className="sp__field-label-required">*</span>
                </label>
                <div className="sp__input-with-suffix">
                  <input
                    type="number"
                    step="0.01"
                    className="sp__input"
                    placeholder="12 or -5"
                    value={form.adjustmentPct}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        adjustmentPct: e.target.value,
                      }))
                    }
                  />
                  <span className="sp__input-suffix">%</span>
                </div>
              </div>
            </div>

            <div>
              <label className="sp__field-label">
                Explainer
                <span style={{ color: "var(--sp-text-tertiary)", marginLeft: 6 }}>
                  (optional — {form.explainer.length}/500)
                </span>
              </label>
              <textarea
                className="sp__input"
                style={{ minHeight: 80, resize: "vertical", padding: "10px 14px" }}
                maxLength={500}
                placeholder="e.g. You're flying along with all the demand, and that's why this costs more than usual."
                value={form.explainer}
                onChange={(e) =>
                  setForm((f) => ({ ...f, explainer: e.target.value }))
                }
              />
            </div>

            <div className="sp__form-row sp__form-row--two">
              <div>
                <label className="sp__field-label">
                  Departure States
                  <span className="sp__field-label-required">*</span>
                </label>
                <Select
                  isMulti
                  options={stateOptions}
                  value={departureValue}
                  isLoading={isLoadingStates}
                  onChange={(selected) =>
                    setForm((f) => ({
                      ...f,
                      departureStates: (selected || []).map((o) => o.value),
                    }))
                  }
                  placeholder="Select departure states"
                  classNamePrefix="select"
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
              <div>
                <label className="sp__field-label">
                  Arrival States
                  <span className="sp__field-label-required">*</span>
                </label>
                <Select
                  isMulti
                  options={stateOptions}
                  value={arrivalValue}
                  isLoading={isLoadingStates}
                  onChange={(selected) =>
                    setForm((f) => ({
                      ...f,
                      arrivalStates: (selected || []).map((o) => o.value),
                    }))
                  }
                  placeholder="Select arrival states"
                  classNamePrefix="select"
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </div>

            <div className="sp__toggle-row">
              <label className="sp__switch">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, active: e.target.checked }))
                  }
                />
                <span className="sp__switch-slider" />
              </label>
              <span
                className="sp__toggle-label"
                onClick={() =>
                  setForm((f) => ({ ...f, active: !f.active }))
                }
              >
                {form.active ? "Rule is active" : "Rule is paused"}
              </span>
            </div>

            <div className="sp__form-actions">
              <button
                type="submit"
                className="sp__primary-btn"
                disabled={isSaving}
              >
                {isSaving
                  ? "Saving…"
                  : isEditing
                  ? "Save Changes"
                  : "Create Rule"}
              </button>
              <button
                type="button"
                className="sp__ghost-btn"
                onClick={handleCancelForm}
                disabled={isSaving}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* LIST */}
      <div className="sp__list-header">
        <div className="sp__list-title">Rules</div>
        {!isLoadingRules && visibleRules.length > 0 && (
          <div className="sp__list-count">
            Showing {visibleRules.length}
            {visibleRules.length !== rules.length
              ? ` of ${rules.length}`
              : ""}
          </div>
        )}
      </div>

      {isLoadingRules ? (
        <div className="sp__loader">
          <div className="sp__loader-spinner" />
          Loading seasonal rules…
        </div>
      ) : visibleRules.length === 0 ? (
        <div className="sp__empty">
          <div className="sp__empty-icon">◇</div>
          <div className="sp__empty-title">
            {rules.length === 0
              ? "No seasonal rules yet"
              : "No rules match this filter"}
          </div>
          <div className="sp__empty-text">
            {rules.length === 0
              ? "Create a rule to adjust estimate pricing for specific date ranges and state pairs."
              : "Try switching to a different filter above, or create a new rule."}
          </div>
        </div>
      ) : (
        <div className="sp__list">
          {visibleRules.map((rule) => {
            const mod = bannerModifier(rule);
            return (
              <div
                key={rule._id}
                className={`sp-rule${
                  !rule.active ? " sp-rule--inactive" : ""
                }`}
              >
                <div className={`sp-rule__banner sp-rule__banner--${mod}`}>
                  <div className="sp-rule__banner-direction">
                    <span className="sp-rule__banner-label">
                      {bannerLabel(rule)}
                    </span>
                  </div>
                  <div className="sp-rule__banner-adjustment">
                    {formatAdjustment(rule.adjustment)}
                  </div>
                </div>

                <div className="sp-rule__body">
                  {/* LEFT: label + dates */}
                  <div className="sp-rule__col">
                    <div className="sp-rule__label">Rule</div>
                    <h3 className="sp-rule__title">{rule.label}</h3>
                    <div className="sp-rule__dates">
                      <div className="sp-rule__date-block">
                        <div className="sp-rule__date-day">
                          {formatMonth(rule.startDate)} {formatDay(rule.startDate)}
                        </div>
                        <div className="sp-rule__date-meta">
                          {formatYear(rule.startDate)}
                        </div>
                      </div>
                      <span className="sp-rule__date-arrow">→</span>
                      <div className="sp-rule__date-block">
                        <div className="sp-rule__date-day">
                          {formatMonth(rule.endDate)} {formatDay(rule.endDate)}
                        </div>
                        <div className="sp-rule__date-meta">
                          {formatYear(rule.endDate)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* MIDDLE: flow arrow */}
                  <div className="sp-rule__flow">
                    <div className="sp-rule__flow-arrow">→</div>
                    <div className="sp-rule__flow-meta">Direction</div>
                  </div>

                  {/* RIGHT: states */}
                  <div className="sp-rule__col">
                    <div className="sp-rule__label">Departure</div>
                    <StateChips states={rule.departureStates} />
                    <div
                      className="sp-rule__label"
                      style={{ marginTop: 14 }}
                    >
                      Arrival
                    </div>
                    <StateChips states={rule.arrivalStates} />
                    {rule.explainer && (
                      <div className="sp-rule__explainer">
                        {rule.explainer}
                      </div>
                    )}
                  </div>
                </div>

                <div className="sp-rule__footer">
                  <div className="sp-rule__footer-meta">
                    <strong>Updated</strong>
                    {rule.updatedAt
                      ? moment(rule.updatedAt).format("M/D/YYYY")
                      : "—"}
                  </div>
                  <button
                    type="button"
                    className="sp-rule__action"
                    onClick={() => handleToggleActive(rule)}
                  >
                    {rule.active ? "Pause" : "Resume"}
                  </button>
                  <button
                    type="button"
                    className="sp-rule__action sp-rule__action--edit"
                    onClick={() => handleEdit(rule)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="sp-rule__action sp-rule__action--danger"
                    onClick={() => handleDelete(rule)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SeasonalPricingContent;
